import Lambda from 'aws-sdk/clients/lambda'
import { API, graphqlOperation } from 'aws-amplify'
import { defaultSettings } from '../components/Admin/Settings/SystemConfiguration/SystemNotifications/DefaultSettings'

// Frontend side of the BSS-SendTeamNotifications lambda. One lambda, one
// `notificationType` per email template from the "Emails and Triggers" sheet.
// The lambda only renders and sends; every recipient list is resolved here.
//
// Sending is always best-effort: the team mutation has already succeeded by
// the time we get here, so a failed email is logged, never surfaced as an
// error on the action the user just completed.

export const TEAM_NOTIFICATION = {
    TEAM_ASSIGNMENT: 'TEAM_ASSIGNMENT',
    REPORT_AS_HERE: 'REPORT_AS_HERE',
    JOIN_TEAM: 'JOIN_TEAM',
    ACCEPT: 'ACCEPT',
    DECLINE: 'DECLINE',
    LEAVE_TEAM: 'LEAVE_TEAM',
    REMOVED_FROM_TEAM: 'REMOVED_FROM_TEAM',
    TRAINING_PROGRAM_YES: 'TRAINING_PROGRAM_YES',
    TRAINING_PROGRAM_NO: 'TRAINING_PROGRAM_NO',
    NEW_POSITION_TRAINING: 'NEW_POSITION_TRAINING',
}

// The three admin notices take their recipients from Building Settings >
// System Notifications (Teams section). Keys are the setting titles in
// DefaultSettings.js; the backend stores them upper-snake-cased in d2.
const NOTICE_SETTING_TITLE = {
    [TEAM_NOTIFICATION.JOIN_TEAM]: 'Send Notice When User Adds Themselves to a Team',
    [TEAM_NOTIFICATION.ACCEPT]: 'Send Notice When User Accepts Team Position',
    [TEAM_NOTIFICATION.DECLINE]: 'Send Notice When User Declines Team Position',
    [TEAM_NOTIFICATION.LEAVE_TEAM]: 'Send Notice When User Removes Themselves from a Team',
}

// Member-facing emails have their own switches on the Settings page too.
const ASSIGNMENT_SETTING_TITLE = 'Notify User to Accept or Decline Team Position'
const REPORT_AS_HERE_SETTING_TITLE = 'Request Team Members to Report as "Here"'

// ---------- lambda invoke ----------

const invokeTeamNotificationLambda = (payload) =>
    new Promise((resolve, reject) => {
        const lambda = new Lambda({
            region: 'us-west-2',
            apiVersion: '2015-03-31',
            credentials: {
                accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY,
                secretAccessKey: process.env.REACT_APP_AWS_SECRET_KEY,
            },
        })

        const params = {
            FunctionName: `arn:aws:lambda:us-west-2:${process.env.REACT_APP_AWS_ACCOUNT_ID}:function:BSS-SendTeamNotifications-${process.env.REACT_APP_STAGE}-${process.env.REACT_APP_AWS_ACCOUNT_ID}`,
            InvocationType: 'RequestResponse',
            Payload: JSON.stringify(payload),
        }

        lambda.invoke(params, (err, data) => {
            if (err) return reject(err)
            if (data.FunctionError) return reject(new Error(`Team notification lambda error: ${data.Payload}`))
            resolve(data)
        })
    })

// Send one notification (or a { batch: [...] } of them). Resolves to the
// lambda's { success, message, successful, failed, skipped } response, or a
// failure-shaped object if the invoke itself failed. Never throws.
export const sendTeamNotification = async (payload) => {
    try {
        const result = await invokeTeamNotificationLambda(payload)
        const response = JSON.parse(result.Payload)
        if (!response?.success) {
            console.warn('Team notification reported failures:', response)
        }
        return response
    } catch (error) {
        console.error('Team notification send failed:', payload?.notificationType || 'batch', error)
        return { success: false, message: error?.message || 'Send failed', successful: 0, failed: 0, skipped: 0 }
    }
}

export const sendTeamNotificationBatch = (items) => {
    const batch = (items || []).filter(Boolean)
    if (batch.length === 0) return Promise.resolve({ success: true, successful: 0, failed: 0, skipped: 0, results: [] })
    return sendTeamNotification({ batch })
}

// ---------- payload helpers ----------

// Team d1 is client#null#property#building (the 4-part building key).
const parseTeamD1 = (teamD1) => {
    const parts = (teamD1 || '').split('#')
    return { propertyPk: parts[2] || null, buildingPk: parts[3] || null }
}

// Deep links for the View Team button. Admins land on the building's Teams
// tab; members/rights-holders land on the occupant Teams page with the team
// preselected (OccupantView reads ?team=).
export const teamLinks = (teamD1, teamPk) => {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://guardian.bssnet.com'
    const { buildingPk } = parseTeamD1(teamD1)
    return {
        admin: buildingPk ? `${origin}/admin/buildings/${buildingPk}#teams` : `${origin}/admin`,
        occupant: teamPk ? `${origin}/teams?team=${encodeURIComponent(teamPk)}` : `${origin}/teams`,
    }
}

export const personOf = (u) => ({
    firstName: u?.firstName || '',
    lastName: u?.lastName || '',
    email: u?.email || '',
})

export const senderInfoOf = (u) => ({ ...personOf(u), sentAt: new Date().toISOString() })

// Support emails carry a contact block. Team Contact is a full person record;
// createdBy/modifiedBy are stored on the team as display-name strings, so
// they go across as { name } unless the caller knows the actual user.
export const teamContactsBlock = ({ team, createdBy, modifiedBy }) => ({
    teamContact: personOf(team?.contact),
    createdBy: createdBy ? personOf(createdBy) : { name: team?.createdBy || '' },
    modifiedBy: modifiedBy ? personOf(modifiedBy) : { name: team?.modifiedBy || '' },
})

const teamBlock = ({ team, teamD1, buildingName, link }) => ({
    name: team?.name || '',
    buildingName: buildingName || '',
    viewTeamUrl: link || teamLinks(teamD1, team?.pk).admin,
})

// ---------- high-level senders ----------

// TRAINING_PROGRAM_YES / TRAINING_PROGRAM_NO / NEW_POSITION_TRAINING → support.
export const notifySupport = ({ type, team, teamD1, buildingName, position, contacts, currentUser }) =>
    sendTeamNotification({
        notificationType: type,
        team: teamBlock({ team, teamD1, buildingName }),
        position: position || undefined,
        contacts,
        senderInfo: senderInfoOf(currentUser),
    })

// TEAM_ASSIGNMENT to one member, plus REPORT_AS_HERE when the team requires
// it. `member` needs firstName/lastName/email and the position title.
export const notifyAssignment = async ({ team, teamD1, buildingName, assignedBy, member, includeReportAsHere }) => {
    const links = teamLinks(teamD1, team?.pk)
    const recipient = { ...personOf(member), viewTeamUrl: links.occupant }
    const sender = senderInfoOf(assignedBy)
    const [assignmentOn, reportOn] = await Promise.all([
        isTeamSettingEnabled(ASSIGNMENT_SETTING_TITLE, teamD1),
        includeReportAsHere ? isTeamSettingEnabled(REPORT_AS_HERE_SETTING_TITLE, teamD1) : false,
    ])
    return sendTeamNotificationBatch([
        assignmentOn
            ? {
                notificationType: TEAM_NOTIFICATION.TEAM_ASSIGNMENT,
                team: teamBlock({ team, teamD1, buildingName, link: links.occupant }),
                actor: personOf(assignedBy),
                member: { ...personOf(member), position: member?.position || '' },
                recipients: [recipient],
                senderInfo: sender,
            }
            : null,
        reportOn
            ? {
                notificationType: TEAM_NOTIFICATION.REPORT_AS_HERE,
                team: teamBlock({ team, teamD1, buildingName, link: links.occupant }),
                member: personOf(member),
                recipients: [recipient],
                senderInfo: sender,
            }
            : null,
    ])
}

// TEAM_ASSIGNMENT reminder to a chosen list (the Send Notifications button).
export const notifyAssignmentReminder = async ({ team, teamD1, buildingName, sender, members }) => {
    if (!(await isTeamSettingEnabled(ASSIGNMENT_SETTING_TITLE, teamD1))) {
        console.log('Team assignment notice is disabled in System Notifications — not sent.')
        return { success: false, message: 'Disabled in System Notifications', successful: 0, failed: 0, skipped: 0 }
    }
    const links = teamLinks(teamD1, team?.pk)
    const recipients = dedupeByEmail(
        (members || []).map((m) => ({ ...personOf(m), position: m?.position || '', viewTeamUrl: links.occupant }))
    )
    if (recipients.length === 0) return Promise.resolve({ success: false, message: 'No recipients', successful: 0, failed: 0, skipped: 0 })
    // One call per member so each email carries that member's own position.
    return sendTeamNotificationBatch(
        recipients.map((r) => ({
            notificationType: TEAM_NOTIFICATION.TEAM_ASSIGNMENT,
            team: teamBlock({ team, teamD1, buildingName, link: links.occupant }),
            actor: personOf(sender),
            member: { ...personOf(r), position: r.position },
            recipients: [r],
            senderInfo: senderInfoOf(sender),
        }))
    )
}

// REPORT_AS_HERE to every current member (Team Details flipped the question
// to Yes).
export const notifyReportAsHere = async ({ team, teamD1, buildingName, sender, members }) => {
    if (!(await isTeamSettingEnabled(REPORT_AS_HERE_SETTING_TITLE, teamD1))) {
        console.log('Report-as-here notice is disabled in System Notifications — not sent.')
        return { success: true, successful: 0, failed: 0, skipped: 0 }
    }
    const links = teamLinks(teamD1, team?.pk)
    const recipients = dedupeByEmail((members || []).map((m) => ({ ...personOf(m), viewTeamUrl: links.occupant })))
    if (recipients.length === 0) return Promise.resolve({ success: true, successful: 0, failed: 0, skipped: 0 })
    return sendTeamNotification({
        notificationType: TEAM_NOTIFICATION.REPORT_AS_HERE,
        team: teamBlock({ team, teamD1, buildingName, link: links.occupant }),
        recipients,
        senderInfo: senderInfoOf(sender),
    })
}

// REMOVED_FROM_TEAM → the member an admin or rights-holder just archived.
// Not in the "Emails and Triggers" sheet, but the Settings page carries
// "Notify User They Have Been Removed From Team", so that toggle gates it.
const REMOVED_SETTING_TITLE = 'Notify User They Have Been Removed From Team'

export const notifyRemovedFromTeam = async ({ team, teamD1, buildingName, removedBy, member, position }) => {
    if (!member?.email) return { success: true, successful: 0, failed: 0, skipped: 0 }
    const enabled = await isTeamSettingEnabled(REMOVED_SETTING_TITLE, teamD1)
    if (!enabled) {
        console.log('Removed-from-team notice is disabled in System Notifications — not sent.')
        return { success: true, successful: 0, failed: 0, skipped: 0 }
    }
    const links = teamLinks(teamD1, team?.pk)
    return sendTeamNotification({
        notificationType: TEAM_NOTIFICATION.REMOVED_FROM_TEAM,
        team: teamBlock({ team, teamD1, buildingName, link: links.occupant }),
        actor: personOf(removedBy),
        member: { ...personOf(member), position: position || '' },
        recipients: [{ ...personOf(member), viewTeamUrl: links.occupant }],
        senderInfo: senderInfoOf(removedBy),
    })
}

// JOIN_TEAM / ACCEPT / DECLINE / LEAVE_TEAM → recipients from System
// Notification settings. `actor` is the redux user who joined/accepted/
// declined/left.
export const notifyTeamNotice = async ({ type, team, teamD1, buildingName, actor, actorPosition, members }) => {
    const { enabled, recipients } = await resolveTeamNoticeRecipients({ type, team, teamD1, actor, members })
    if (!enabled) {
        console.log(`Team notice ${type} is disabled in System Notifications — not sent.`)
        return { success: true, successful: 0, failed: 0, skipped: 0 }
    }
    if (recipients.length === 0) {
        console.warn(`Team notice ${type}: no recipients resolved — not sent.`)
        return { success: true, successful: 0, failed: 0, skipped: 0 }
    }
    return sendTeamNotification({
        notificationType: type,
        team: teamBlock({ team, teamD1, buildingName }),
        actor: { ...personOf(actor), position: actorPosition || '' },
        member: { ...personOf(actor), position: actorPosition || '' },
        recipients,
        senderInfo: senderInfoOf(actor),
    })
}

// ---------- recipient resolution for admin notices ----------

const LIST_NOTIFICATION_SETTINGS = `query ListNotificationSettings($sk: String!, $d1: String!) {
    listNotificationSettings(sk: $sk, d1: $d1) {
        items { d2 enabled methods { email } recipients { roles emails } }
    }
}`

const LIST_ADMINS = `query ($sk: String, $pk: String) {
    listAdminAndContacts(sk: $sk, pk: $pk) {
        __typename
        ... on Admin { email firstName lastName }
        ... on Contact { email firstName lastName }
    }
}`

const LIST_FSDS = `query ($sk: String, $pk: String) {
    listFireSafetyDirectors(sk: $sk, pk: $pk) { email firstName lastName }
}`

const LIST_TEAM_MEMBERS = `query ListTeamMembers($pk: ID) {
    listTeamMembers(pk: $pk) {
        items { firstName lastName email tenant floor positionId acceptedOn }
    }
}`

const GET_TEAM_POSITIONS = `query GetTeam($pk: ID) {
    getTeam(pk: $pk) {
        contact { firstName lastName email }
        positions { sk managementRights }
    }
}`

const settingKey = (title) => title.replace(/ /g, '_').toUpperCase()

// A saved setting sends email unless the admin switched the notice off (the
// list-page toggle) or switched Email off inside it (the Delivery toggle).
const settingSendsEmail = (stored) =>
    stored.enabled !== false && stored.methods?.email !== false

// A notice is on unless the building has explicitly saved it off. A lookup
// failure counts as on so a permissions hiccup never silently drops mail.
const isTeamSettingEnabled = async (title, teamD1) => {
    const saved = await tryQuery(
        LIST_NOTIFICATION_SETTINGS,
        { sk: 'NOTIFICATION_SETTING', d1: teamD1 },
        (d) => d.listNotificationSettings?.items
    )
    const stored = saved.find((s) => s.d2 === settingKey(title))
    return !stored || settingSendsEmail(stored)
}

const defaultTeamSetting = (title) =>
    (defaultSettings.find((s) => s.key === 'teams')?.settings || []).find((s) => s.title === title)

// Best-effort GraphQL call: occupants may not be authorized for the admin
// lists. A failure just means that role contributes no recipients.
const tryQuery = async (op, vars, pick) => {
    try {
        const res = await API.graphql(graphqlOperation(op, vars))
        return pick(res.data) || []
    } catch (e) {
        console.warn('Team notice recipient lookup failed:', vars, e?.errors || e)
        return []
    }
}

const positionIdOf = (sk) => (sk || '').split('#').pop()

const dedupeByEmail = (list, exclude = []) => {
    const seen = new Set(exclude.map((e) => (e || '').toLowerCase()).filter(Boolean))
    return (list || []).filter((r) => {
        const email = (r?.email || '').trim().toLowerCase()
        if (!email || !email.includes('@') || seen.has(email)) return false
        seen.add(email)
        return true
    })
}

export const resolveTeamNoticeRecipients = async ({ type, team, teamD1, actor, members }) => {
    const title = NOTICE_SETTING_TITLE[type]
    if (!title) return { enabled: true, recipients: [] }

    const { propertyPk, buildingPk } = parseTeamD1(teamD1)
    const links = teamLinks(teamD1, team?.pk)

    // 1. Saved setting for this building, else the shipped default.
    const saved = await tryQuery(
        LIST_NOTIFICATION_SETTINGS,
        { sk: 'NOTIFICATION_SETTING', d1: teamD1 },
        (d) => d.listNotificationSettings?.items
    )
    const stored = saved.find((s) => s.d2 === settingKey(title))
    if (stored && !settingSendsEmail(stored)) return { enabled: false, recipients: [] }

    const fallback = defaultTeamSetting(title)?.recipients || {}
    const roles = stored?.recipients?.roles || fallback.activeRoles || fallback.roles || []
    const emails = stored?.recipients?.emails || []

    const out = []
    const asAdmin = (u) => ({ ...personOf(u), viewTeamUrl: links.admin })

    // 2. Admin roles and FSDs.
    if (roles.includes('BUILDING_ADMIN') && buildingPk) {
        const rows = await tryQuery(LIST_ADMINS, { sk: 'BUILDING', pk: buildingPk }, (d) => d.listAdminAndContacts)
        rows.filter((r) => r.__typename === 'Admin').forEach((r) => out.push(asAdmin(r)))
    }
    if (roles.includes('PROPERTY_ADMIN') && propertyPk) {
        const rows = await tryQuery(LIST_ADMINS, { sk: 'PROPERTY', pk: propertyPk }, (d) => d.listAdminAndContacts)
        rows.filter((r) => r.__typename === 'Admin').forEach((r) => out.push(asAdmin(r)))
    }
    if (roles.includes('TENANT_ADMIN') && actor?.tenant?.pk) {
        const rows = await tryQuery(LIST_ADMINS, { sk: 'TENANT', pk: actor.tenant.pk }, (d) => d.listAdminAndContacts)
        rows.filter((r) => r.__typename === 'Admin').forEach((r) => out.push(asAdmin(r)))
    }
    if (roles.includes('FIRE_SAFETY_DIRECTOR') && buildingPk) {
        const rows = await tryQuery(LIST_FSDS, { sk: 'BUILDING', pk: buildingPk }, (d) => d.listFireSafetyDirectors)
        rows.forEach((r) => out.push(asAdmin(r)))
    }

    // 3. Team-scoped roles. Both need the team's contact and positions; fetch
    //    them when the caller's team object doesn't carry them.
    const needsTeam = roles.includes('TEAM_PRIMARY_CONTACT') || roles.includes('TEAM_MGMT_RIGHTS')
    let contact = team?.contact
    let positions = team?.positions
    if (needsTeam && (!contact || !positions) && team?.pk) {
        const t = await tryQuery(GET_TEAM_POSITIONS, { pk: team.pk }, (d) => d.getTeam)
        contact = contact || t?.contact
        positions = positions || t?.positions
    }

    if (roles.includes('TEAM_PRIMARY_CONTACT') && contact?.email) {
        out.push(asAdmin(contact))
    }

    if (roles.includes('TEAM_MGMT_RIGHTS') && team?.pk) {
        const rightsByPosition = new Map(
            (positions || []).map((p) => [positionIdOf(p.sk), p.managementRights])
        )
        const list = Array.isArray(members) && members.length > 0
            ? members
            : await tryQuery(LIST_TEAM_MEMBERS, { pk: team.pk }, (d) => d.listTeamMembers?.items)

        const actorFloor = actor?.floor || (actor?.d1 || '').split('#')[5] || ''
        const actorTenant = (actor?.tenant?.name || '').trim().toLowerCase()

        list.forEach((m) => {
            const rights = rightsByPosition.get(positionIdOf(m.positionId))
            if (rights === 'SAME_FLOOR' && actorFloor && String(m.floor || '') === String(actorFloor)) {
                out.push({ ...personOf(m), viewTeamUrl: links.occupant })
            } else if (rights === 'SAME_TENANT' && actorTenant && (m.tenant || '').trim().toLowerCase() === actorTenant) {
                out.push({ ...personOf(m), viewTeamUrl: links.occupant })
            }
        })
    }

    // 4. Explicit addresses typed into the setting.
    emails.forEach((email) => out.push({ email, viewTeamUrl: links.admin }))

    // The person who acted never gets their own notice.
    return { enabled: true, recipients: dedupeByEmail(out, [actor?.email]) }
}
